import {
  ActionName,
  IActionContextAPI,
  NativeActionsList,
} from '@wix/editor-platform-host-integration-apis';
import {
  IVirtualManifestBuilderProps,
  ManifestPropertyFilter,
  ReducibleKey,
} from '@wix/editor-platform-manifest-services';

import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

import { PlatformContext } from '../types';
import { BaseActionPanelAPI } from './BaseActionPanelAPI';
import { IActionContextContributor } from './IActionContextContributor';

import { ReactElement } from 'react';

export class EditTextActionPanelAPI
  extends BaseActionPanelAPI
  implements IActionContextContributor
{
  constructor(readonly context: PlatformContext) {
    super();
  }

  #getPanelProps() {
    return {
      componentsAPI: this.context.componentsAPI,
      editorPointer: this.context.editorPointers[0],
      editorPointerAPI: this.context.editorPointerAPI,
      getSDKHost: async (props?: IVirtualManifestBuilderProps) => {
        const host = await this.getHostContext(props);
        return host.getHost();
      },
    };
  }

  getMinimizedContent() {
    const editTextAPI =
      this.context.editorPlatformPackagesBridge?.PlatformEditTextPanelAPI;

    const props = this.#getPanelProps();

    return editTextAPI?.getMinimizedContent?.(props) as ReactElement;
  }

  getContent(): ReactElement | undefined {
    const editTextAPI =
      this.context.editorPlatformPackagesBridge?.PlatformEditTextPanelAPI;

    const props = this.#getPanelProps();

    return editTextAPI?.getPanelContent?.(props) as ReactElement;
  }

  getActionContextOverrides(): Partial<IActionContextAPI> {
    return {
      getActionableElements: () => {
        const textDataItems =
          this.context.componentsAPI.editActions.getActionDataItems(
            NativeActionsList.editText,
            this.context.editorPointers,
          );

        if (!textDataItems.length) {
          return this.context.componentsAPI.actionableElements.getActionableElements(
            this.context.editorPointers,
            NativeActionsList.editText as ActionName,
          );
        }

        return {
          elements: textDataItems.map((item) => ({
            editorPointers: this.context.editorPointers,
            displayName: item.displayName ?? undefined,
            elements: [],
            isActionable: true,
          })),
        };
      },
    };
  }

  async getHostContext(props?: IVirtualManifestBuilderProps) {
    const filter: ManifestPropertyFilter = (
      manifestProp: ReducibleKey,
      _key: string,
      definition: any,
    ) =>
      manifestProp === 'data' &&
      (definition as any)?.dataType === DataType.text;

    return this.getInfraSDKHostContext({
      ...props,
      editorPointers: props?.editorPointers ?? [this.context.editorPointer],
      contextBuilder: (builder) => {
        if (props?.contextBuilder) {
          props.contextBuilder(builder);
        }

        builder.withFilters(filter);
      },
    });
  }
}
